import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';
import { parseCacheControlMaxAgeMs } from '../utils/transportCompanyLogoValidation';
import {
    TRANSPORT_COMPANY_LOGO_FORMATS,
    TRANSPORT_COMPANY_LOGO_TTL_MS,
} from '../constants/transportCompanyLogo';

const cache = new Map();
const inflight = new Map();
const listeners = new Set();

function normalizeFormat(format) {
    const value = String(format || '').toUpperCase();
    if (value !== TRANSPORT_COMPANY_LOGO_FORMATS.PNG && value !== TRANSPORT_COMPANY_LOGO_FORMATS.SVG) {
        throw new Error('formato deve ser PNG ou SVG.');
    }
    return value;
}

function notify() {
    listeners.forEach((listener) => listener());
}

function revokeEntry(entry) {
    if (entry?.objectUrl) {
        URL.revokeObjectURL(entry.objectUrl);
    }
}

function setCacheEntry(format, entry) {
    const previous = cache.get(format);
    if (previous?.objectUrl && previous.objectUrl !== entry.objectUrl) {
        revokeEntry(previous);
    }
    cache.set(format, entry);
}

export function subscribeTransportCompanyLogoCache(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getCachedTransportCompanyLogo(format) {
    try {
        return cache.get(normalizeFormat(format)) ?? null;
    } catch {
        return null;
    }
}

export function hasFreshTransportCompanyLogoCache(format) {
    const entry = getCachedTransportCompanyLogo(format);
    return Boolean(entry && Date.now() < entry.expiresAt);
}

export function invalidateTransportCompanyLogoCache() {
    cache.forEach((entry) => revokeEntry(entry));
    cache.clear();
    inflight.clear();
    notify();
}

function acceptFor(format) {
    return format === TRANSPORT_COMPANY_LOGO_FORMATS.SVG ? 'image/svg+xml' : 'image/png';
}

async function fetchLogoPublic(path, format) {
    return httpClient.get(path, {
        skipAuth: true,
        headers: { Accept: acceptFor(format) },
    });
}

async function fetchLogoAuthenticated(path, format) {
    const token = httpClient.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    if (!token) return null;

    // skipAuth evita o interceptor de 403/refresh; o Bearer é enviado só nesta tentativa.
    return httpClient.get(path, {
        skipAuth: true,
        headers: {
            Accept: acceptFor(format),
            Authorization: `Bearer ${token}`,
        },
    });
}

async function fetchLogo(format, { cacheBust = false } = {}) {
    const params = new URLSearchParams({ formato: format });
    if (cacheBust) {
        params.set('t', String(Date.now()));
    }
    const path = `/dados/logo?${params.toString()}`;

    let response = await fetchLogoPublic(path, format);
    if (response.status === 401 || response.status === 403) {
        response = await fetchLogoAuthenticated(path, format) ?? response;
    }

    const ttlMs = parseCacheControlMaxAgeMs(
        response.headers.get('Cache-Control'),
        TRANSPORT_COMPANY_LOGO_TTL_MS,
    );
    const expiresAt = Date.now() + Math.max(ttlMs, 0);

    if (response.status === 404) {
        return { format, missing: true, objectUrl: null, expiresAt };
    }

    if (!response.ok) {
        throw await parseApiError(response);
    }

    const blob = await response.blob();
    return {
        format,
        missing: false,
        objectUrl: URL.createObjectURL(blob),
        expiresAt,
    };
}

async function get(format, { force = false } = {}) {
    const key = normalizeFormat(format);
    if (!force && hasFreshTransportCompanyLogoCache(key)) {
        return cache.get(key);
    }
    if (!force && inflight.has(key)) {
        return inflight.get(key);
    }

    const request = fetchLogo(key, { cacheBust: force })
        .then((entry) => {
            setCacheEntry(key, entry);
            return entry;
        })
        .finally(() => {
            inflight.delete(key);
        });

    inflight.set(key, request);
    return request;
}

async function update({ png, svg }) {
    const formData = new FormData();
    formData.append('png', png);
    formData.append('svg', svg);

    const response = await httpClient.request('/dados/logo', {
        method: 'PUT',
        body: formData,
    });

    if (!response.ok) {
        throw await parseApiError(response);
    }

    invalidateTransportCompanyLogoCache();
    return response;
}

const transportCompanyLogoService = {
    get,
    update,
    invalidateCache: invalidateTransportCompanyLogoCache,
    getCached: getCachedTransportCompanyLogo,
    hasFreshCache: hasFreshTransportCompanyLogoCache,
    subscribe: subscribeTransportCompanyLogoCache,
};

export default transportCompanyLogoService;
